import React from "react";
import AppShell from "../components/AppShell";
import {
  Lightbulb,
  Beef,
  Moon,
  Pill,
  Footprints,
  Flame,
  Eye,
  Droplet,
  Sparkles,
} from "lucide-react";

const TIPS = [
  {
    icon: Beef,
    tone: "pink",
    title: "Prioriza la proteína.",
    body: [
      "Consumir suficiente proteína ayuda a conservar y desarrollar masa muscular, aumenta la saciedad y favorece la recuperación después del entrenamiento.",
    ],
  },
  {
    icon: Moon,
    tone: "gold",
    title: "Duerme al menos 8 horas cada noche.",
    body: [
      "Durante el sueño tu cuerpo se recupera, regula hormonas, repara tejido muscular y mejora el rendimiento físico y mental.",
      "Dormir poco puede aumentar el hambre, reducir la energía y dificultar la pérdida de grasa.",
    ],
  },
  {
    icon: Pill,
    tone: "pink",
    title: "Toma tus suplementos de forma inteligente.",
    body: [
      "La sección \"Suplementos por edades\" de esta aplicación fue creada para orientarte según tu etapa de vida.",
      "Los suplementos no sustituyen una alimentación saludable, pero pueden ayudarte a cubrir necesidades específicas cuando son utilizados correctamente.",
    ],
  },
  {
    icon: Footprints,
    tone: "gold",
    title: "Muévete más durante el día.",
    body: [
      "Si hoy no puedes ir al gimnasio, intenta caminar al menos 20 minutos diarios.",
      "Cada paso cuenta. La actividad física mejora la circulación, ayuda a quemar calorías, mejora el estado de ánimo y favorece la salud cardiovascular.",
    ],
  },
  {
    icon: Flame,
    tone: "pink",
    title: "Para perder grasa necesitas crear un déficit calórico.",
    body: [
      "En términos simples: debes gastar más calorías de las que consumes.",
      "Esto no significa pasar hambre. Significa aprender a comer mejor, controlar las porciones y mantenerte activo.",
    ],
  },
  {
    icon: Eye,
    tone: "gold",
    title: "No te obsesiones con la báscula.",
    body: ["El peso es solo un número. También observa:"],
    list: [
      "Cómo te queda la ropa.",
      "Tus medidas corporales.",
      "Tu nivel de energía.",
      "Tu fuerza en los entrenamientos.",
      "Tus fotografías de progreso.",
    ],
    footer: "Muchas veces los cambios más importantes ocurren antes de verse reflejados en la báscula.",
  },
  {
    icon: Droplet,
    tone: "pink",
    title: "La hidratación es fundamental.",
    body: [
      "Tomar suficiente agua ayuda al metabolismo, la digestión, la recuperación muscular y el rendimiento físico.",
      "Muchas veces la sensación de hambre es simplemente sed.",
    ],
  },
];

export default function Tips() {
  return (
    <AppShell title="Consejos importantes" subtitle="Pequeños hábitos, grandes resultados.">
      <div className="space-y-8 max-w-5xl" data-testid="tips-page">

        {/* HERO */}
        <section className="relative overflow-hidden rounded-2xl border border-[var(--mep-border)]">
          <div className="grid md:grid-cols-2">
            <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[340px]">
              <img
                src="/generated/tips_hero.jpg"
                alt="Hábitos saludables — libreta, agua, mancuerna y frutas"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/75 via-black/25 to-transparent" />
            </div>
            <div className="p-7 md:p-10 flex flex-col justify-center bg-gradient-to-br from-[#0d0d0f] via-[#0a0a0c] to-[#11090d]">
              <div className="w-12 h-12 rounded-full bg-[rgba(212,175,55,0.12)] border border-[var(--mep-gold)]/40 flex items-center justify-center text-[var(--mep-gold)] mb-4">
                <Lightbulb size={22} />
              </div>
              <div className="text-eyebrow text-[var(--mep-gold)] mb-3">Filosofía del cambio</div>
              <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight leading-snug">
                Los pequeños hábitos diarios son los que crean los grandes resultados.
              </h2>
              <p className="text-sm text-[var(--mep-text-muted)] mt-4 leading-relaxed">
                No necesitas ser perfecto. Necesitas ser constante.
              </p>
            </div>
          </div>
        </section>

        {/* TIPS */}
        <div className="grid md:grid-cols-2 gap-5">
          {TIPS.map((tip, i) => (
            <TipCard key={i} index={i + 1} {...tip} />
          ))}
        </div>

        {/* MOTIVACIONAL FINAL */}
        <section
          className="relative overflow-hidden rounded-2xl border border-[var(--mep-gold)]/40 bg-gradient-to-br from-[#1a1115] via-[#0f0a0c] to-[#13090e] p-8 md:p-12 text-center"
          data-testid="tips-final-motivational"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[rgba(232,61,132,0.10)] blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[rgba(212,175,55,0.08)] blur-3xl pointer-events-none" />

          <Sparkles size={26} className="text-[var(--mep-gold)] mx-auto mb-5 relative" />
          <div className="relative max-w-2xl mx-auto space-y-4 text-[var(--mep-text)] leading-relaxed">
            <p className="font-display text-xl md:text-2xl font-light tracking-tight">
              No importa cuántas veces hayas empezado antes.
            </p>
            <p className="text-sm md:text-base text-[var(--mep-text-muted)]">
              Lo importante es que hoy has decidido volver a intentarlo.
            </p>
            <p className="text-sm md:text-base text-[var(--mep-text-muted)]">
              Habrá días buenos y días difíciles, pero cada entrenamiento, cada comida saludable y cada decisión correcta te acercan a la mejor versión de ti mismo.
            </p>
            <p className="text-sm md:text-base text-[var(--mep-text-muted)]">
              No busques cambios rápidos. Busca hábitos que puedas mantener durante toda la vida.
            </p>
            <p className="pt-3 font-display text-lg md:text-xl text-[var(--mep-pink)]">
              La constancia siempre vence a la perfección.
            </p>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

const TipCard = ({ index, icon: Icon, tone, title, body, list, footer }) => {
  const accent = tone === "pink" ? "var(--mep-pink)" : "var(--mep-gold)";
  return (
    <article
      data-testid={`tip-card-${index}`}
      className="relative mep-card p-6 md:p-7 flex flex-col gap-4 border-l-2"
      style={{ borderLeftColor: accent }}
    >
      <div className="flex items-center gap-4">
        <div
          className="w-11 h-11 flex-shrink-0 rounded-full flex items-center justify-center"
          style={{
            background: `rgba(${tone === "pink" ? "232,61,132" : "212,175,55"}, 0.12)`,
            border: `1px solid ${accent}40`,
            color: accent,
          }}
        >
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-eyebrow" style={{ color: accent }}>
            Consejo #{index}
          </div>
          <h3 className="font-display text-lg md:text-xl font-light tracking-tight mt-1 leading-snug">
            {title}
          </h3>
        </div>
      </div>

      <div className="space-y-2.5 text-sm text-[var(--mep-text-muted)] leading-relaxed">
        {body.map((p, i) => <p key={i}>{p}</p>)}
        {list && (
          <ul className="space-y-1.5 mt-2">
            {list.map((it, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span
                  className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: accent }}
                />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        )}
        {footer && (
          <p className="pt-3 mt-2 border-t border-[var(--mep-border)] text-xs italic" style={{ color: accent }}>
            {footer}
          </p>
        )}
      </div>
    </article>
  );
};
